import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDateTime, titleCase } from '../../lib/format'
import type { ApprovalRequest } from '../../types'
import { Alert, Badge, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge } from '../../components/ui'
import { CheckIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/approvals_queue.html + its
 * view admin/routes.py:approvals_queue(). The generic maker-checker queue
 * (distinct from the compliance/org-onboarding queue, Group C) - reuses the
 * same GET /api/v1/ops/approvals endpoint web/src/pages/ops/Approvals.tsx
 * already consumes, adding the two admin-only affordances the ops page
 * doesn't need: the Private queue tab and a link out to a fuller detail
 * view (payload dump, cancel-own-request). */

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
]

interface ApprovalsResponse { ok: boolean; items: ApprovalRequest[]; status: string }

export default function AdminApprovals() {
  const [status, setStatus] = useState('pending')
  const [privateView, setPrivateView] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'approvals', status, privateView],
    queryFn: () => api.get<ApprovalsResponse>(`/ops/approvals?status=${status}${privateView ? '&private=1' : ''}`),
  })

  // Only used for the Private tab's pending-count badge - a super_admin
  // always sees the private queue, so this is a cheap always-on peek.
  const privatePendingQuery = useQuery({
    queryKey: ['admin', 'approvals', 'private-pending-count'],
    queryFn: () => api.get<ApprovalsResponse>('/ops/approvals?status=pending&private=1'),
  })
  const privatePendingCount = privatePendingQuery.data?.items.length ?? 0

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Approvals</h1>
          <p className="page-head__sub">Maker-checker review queue for sensitive platform changes.</p>
        </div>
      </div>

      <div className="row" style={{ gap: 'var(--sp-2)', marginBottom: 'var(--sp-4)' }}>
        <button
          className={`btn btn--sm ${!privateView ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setPrivateView(false)}
        >
          All requests
        </button>
        <button
          className={`btn btn--sm ${privateView ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setPrivateView(true)}
        >
          Private queue
          {privatePendingCount > 0 && <Badge tone="danger" dot>{privatePendingCount}</Badge>}
        </button>
      </div>

      <Card>
        <CardHeader
          title="Requests"
          action={(
            <div className="tab-scroll" style={{ gap: 4 }}>
              {STATUS_TABS.map((t) => (
                <button
                  key={t.value}
                  className={`btn btn--sm ${status === t.value ? 'btn--primary' : 'btn--ghost'}`}
                  onClick={() => setStatus(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<CheckIcon size={22} />} title="Nothing here" body="Requests in this state will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Request</th><th>Kind</th><th>Submitted by</th><th>When</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.summary || r.kind_label || titleCase(r.kind)}
                        {r.is_private && <Badge tone="danger" dot>PRIVATE</Badge>}
                      </td>
                      <td>{r.kind_label || titleCase(r.kind)}</td>
                      <td>{r.requested_by_name || (r.requested_by ?? '').slice(0, 8) || '—'}</td>
                      <td>{formatDateTime(r.requested_at)}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td style={{ textAlign: 'right' }}>
                        <Link to={`/approvals/${r.id}`}>Review →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
